import React from "react";
import {
  Smartphone,
  Building2,
  CheckCircle2,
  Circle,
  Loader2,
} from "lucide-react";
import Select from "react-select";

const CHANNEL_ICONS = {
  mobile_money: Smartphone,
  bank_transfer: Building2,
  sepa: Building2,
  fps: Building2,
  ach: Building2,
  wire: Building2,
  pix: Smartphone,
};

const SectionLabel = ({ children }) => (
  <h2 className="text-[9px] font-semibold uppercase tracking-[0.2em] text-[#0a2540]/40">
    {children}
  </h2>
);

const FieldLabel = ({ children }) => (
  <label className="block text-[10px] uppercase tracking-[0.18em] font-semibold text-[#0A4730]/70 mb-2">
    {children}
  </label>
);

const INPUT_CLASS =
  "w-full rounded-xl bg-white border border-slate-200 px-4 py-3 text-sm text-[#0A2540] placeholder:text-slate-400 focus:outline-none focus:border-[#0A4730] focus:ring-4 focus:ring-[#0A4730]/5 transition-all duration-200";

const selectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: 46,
    borderRadius: 12,
    backgroundColor: "#FFFFFF",
    border: state.isFocused
      ? "1.5px solid #0A4730"
      : "1px solid #E2E8F0",
    boxShadow: state.isFocused
      ? "0 0 0 4px rgba(10, 71, 48, 0.08)"
      : "0 1px 2px rgba(0,0,0,0.03)",
    fontFamily: "inherit",
    fontSize: 14,
    paddingLeft: 4,
    cursor: "pointer",
    "&:hover": {
      borderColor: "#0A4730",
    },
    transition: "all 0.2s ease",
  }),

  menu: (base) => ({
    ...base,
    borderRadius: 16,
    overflow: "hidden",
    zIndex: 999,
    boxShadow:
      "0 8px 30px rgba(10,71,48,0.12)",
    border: "1px solid #0A473015",
  }),

  menuList: (base) => ({
    ...base,
    padding: 6,
    maxHeight: 220,
  }),

  option: (base, state) => ({
    ...base,
    backgroundColor: state.isSelected
      ? "#0A4730"
      : state.isFocused
        ? "#F5F9F4"
        : "white",
    color: state.isSelected
      ? "white"
      : "#0A2540",
    borderRadius: 10,
    fontSize: 14,
    padding: "10px 12px",
    cursor: "pointer",
    transition: "background 0.1s",
  }),

  input: (base) => ({
    ...base,
    fontSize: 14,
    color: "#0A2540",
  }),

  placeholder: (base) => ({
    ...base,
    fontSize: 14,
    color: "#0A254050",
  }),

  singleValue: (base) => ({
    ...base,
    fontSize: 14,
    color: "#0A2540",
  }),

  indicatorSeparator: () => ({
    display: "none",
  }),

  dropdownIndicator: (base, state) => ({
    ...base,
    color: state.isFocused
      ? "#0A4730"
      : "#0A254040",
    transition:
      "color 0.15s, transform 0.2s",
    transform:
      state.selectProps.menuIsOpen
        ? "rotate(180deg)"
        : "rotate(0deg)",
    paddingRight: 12,
  }),

  noOptionsMessage: (base) => ({
    ...base,
    fontSize: 13,
    color: "#0A254060",
    padding: "12px 8px",
  }),
};

export function RecipientForm({
  nickname,
  setNickname,

  firstName,
  setFirstName,

  lastName,
  setLastName,

  corridor,

  countryOptions,
  selectedCountry,

  channelEntries,

  recipientDialCode,

  banks = [],
  isLoadingBanks = false,

  submitting = false,
  submitError = null,

  onSave,
  onSend,

  showSaveAction = true,
  saveLabel = "Save",
  sendLabel = "Send",

  compact = false,
}) {
  const contentClass = compact
    ? "space-y-5"
    : "space-y-8";

  const cardClass = compact
    ? "bg-white rounded-2xl p-4 border border-slate-100 space-y-4"
    : "bg-white rounded-3xl p-6 border border-slate-100 shadow-[0_4px_20px_-5px_rgba(0,0,0,0.05)] space-y-4";

  return (
    <div className={contentClass}>
      {/* Recipient Details */}
      <div className="space-y-2.5">
        <SectionLabel>
          Recipient Details
        </SectionLabel>

        <div className={cardClass}>
          <div>
            <FieldLabel>
              Nickname (optional)
            </FieldLabel>

            <input
              type="text"
              value={nickname}
              onChange={(event) =>
                setNickname(
                  event.target.value
                )
              }
              placeholder="e.g. Mum"
              className={INPUT_CLASS}
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <FieldLabel>
                First Name
              </FieldLabel>

              <input
                type="text"
                value={firstName}
                onChange={(event) =>
                  setFirstName(
                    event.target.value
                  )
                }
                placeholder="Martha"
                className={INPUT_CLASS}
              />
            </div>

            <div>
              <FieldLabel>
                Last Name
              </FieldLabel>

              <input
                type="text"
                value={lastName}
                onChange={(event) =>
                  setLastName(
                    event.target.value
                  )
                }
                placeholder="Nderitu"
                className={INPUT_CLASS}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Destination */}
      <div className="space-y-2.5">
        <SectionLabel>
          Destination
        </SectionLabel>

        <div className={cardClass}>
          <FieldLabel>
            Country / Region
          </FieldLabel>

          <Select
            options={countryOptions}
            styles={selectStyles}
            value={selectedCountry}
            onChange={(option) =>
              corridor.setCountryCode(
                option?.value || ""
              )
            }
            isSearchable
            placeholder="Search country..."
            noOptionsMessage={() =>
              "No country found"
            }
          />
        </div>
      </div>

      {/* Payout channel */}
      {corridor.countryCode && (
        <div className="space-y-2.5">
          <SectionLabel>
            Mode of Receiving
          </SectionLabel>

          <div className={cardClass}>
            {channelEntries.length ===
            0 ? (
              <p className="text-sm text-[#0A4730]/50">
                No payout channels
                configured for this
                country yet.
              </p>
            ) : (
              <div className="space-y-2.5">
                {channelEntries.map(
                  ([key, channel]) => {
                    const Icon =
                      CHANNEL_ICONS[key] ||
                      Building2;

                    return (
                      <SelectionCard
                        key={key}
                        active={
                          corridor.channelKey ===
                          key
                        }
                        onClick={() =>
                          corridor.setChannelKey(
                            key
                          )
                        }
                        icon={
                          <Icon
                            size={18}
                          />
                        }
                        label={
                          channel.label
                        }
                      />
                    );
                  }
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Dynamic recipient fields */}
      {corridor.activeChannel && (
        <div className="space-y-2.5">
          <SectionLabel>
            Payment Details
          </SectionLabel>

          <div className={cardClass}>
            {corridor.recipientFields.map(
              (field) => (
                <DynamicField
                  key={field.key}
                  field={field}
                  value={
                    corridor.values[
                      field.key
                    ] || ""
                  }
                  onChange={(value) =>
                    corridor.setValue(
                      field.key,
                      value
                    )
                  }
                  errorMessage={
                    corridor.errors[
                      field.key
                    ]
                  }
                  banks={
                    field.type ===
                      "bank_select" &&
                    field.source?.startsWith(
                      "zuba_banks_"
                    )
                      ? banks
                      : undefined
                  }
                  banksLoading={
                    field.type ===
                      "bank_select" &&
                    field.source?.startsWith(
                      "zuba_banks_"
                    )
                      ? isLoadingBanks
                      : false
                  }
                  dialCode={
                    recipientDialCode
                  }
                />
              )
            )}
          </div>
        </div>
      )}

      {submitError && (
        <div className="rounded-2xl bg-red-50 border border-red-100 p-4 text-red-500 text-sm">
          {submitError}
        </div>
      )}

      {/* Actions */}
      <div
        className={
          showSaveAction
            ? "flex gap-3"
            : "flex justify-center"
        }
      >
        {showSaveAction && (
          <button
            type="button"
            disabled={submitting}
            onClick={onSave}
            className="flex-1 h-14 rounded-2xl bg-white text-[#0A4730] border-2 border-[#0A4730] font-bold tracking-wide hover:bg-[#0A4730] hover:text-white transition-all duration-300 active:scale-[0.98] disabled:border-slate-200 disabled:text-slate-400 flex items-center justify-center gap-2"
          >
            {submitting && (
              <Loader2
                size={16}
                className="animate-spin"
              />
            )}

            {saveLabel}
          </button>
        )}

        <button
          type="button"
          disabled={submitting}
          onClick={onSend}
          className={`h-14 rounded-2xl bg-[#0A4730] text-white font-bold tracking-wide hover:bg-[#0A4730]/90 transition-all duration-300 active:scale-[0.98] disabled:opacity-60 flex items-center justify-center gap-2 ${
            showSaveAction
              ? "flex-1"
              : "w-[220px] max-w-full"
          }`}
        >
          {submitting && (
            <Loader2
              size={16}
              className="animate-spin"
            />
          )}

          {sendLabel}
        </button>
      </div>
    </div>
  );
}

const ErrorText = ({ children }) => (
  <p className="text-xs text-red-500 mt-1.5">
    {children}
  </p>
);

function DynamicField({
  field,
  value,
  onChange,
  errorMessage,
  banks,
  banksLoading,
  dialCode,
}) {
  if (field.type === "hidden") {
    return null;
  }

  const hasError =
    Boolean(errorMessage);

  const inputClass = `${INPUT_CLASS} ${
    hasError
      ? "!border-red-400 ring-2 ring-red-200 focus:!border-red-400 focus:ring-red-200"
      : ""
  }`;

  /*
   * Keep the local phone number in UI state.
   * useCorridor() converts it to international
   * format when the payload is built.
   */
  if (
    field.key === "mobile_number"
  ) {
    return (
      <div>
        <FieldLabel>
          {field.label}
        </FieldLabel>

        <div className="flex gap-2">
          <div className="flex items-center justify-center bg-white rounded-xl px-4 min-w-[76px] text-[#0A2540] font-semibold text-sm border border-slate-200">
            {dialCode || "+--"}
          </div>

          <input
            type="tel"
            value={value}
            onChange={(event) =>
              onChange(
                event.target.value.replace(
                  /\D/g,
                  ""
                )
              )
            }
            placeholder={
              field.placeholder ||
              "712345678"
            }
            className={inputClass}
            inputMode="numeric"
            autoComplete="tel-national"
          />
        </div>

        {hasError && (
          <ErrorText>
            {errorMessage}
          </ErrorText>
        )}
      </div>
    );
  }

  if (field.type === "enum") {
    return (
      <div>
        <FieldLabel>
          {field.label}
        </FieldLabel>

        <Select
          options={field.values.map(
            (option) => ({
              value: option,
              label: option,
            })
          )}
          styles={selectStyles}
          value={
            value
              ? {
                  value,
                  label: value,
                }
              : null
          }
          onChange={(option) =>
            onChange(
              option?.value || ""
            )
          }
          placeholder={`Select ${field.label.toLowerCase()}`}
          noOptionsMessage={() =>
            "No options"
          }
        />

        {hasError && (
          <ErrorText>
            {errorMessage}
          </ErrorText>
        )}
      </div>
    );
  }

  if (
    field.type === "bank_select"
  ) {
    if (banks !== undefined) {
      const options = banks.map(
        (bank) => ({
          value: bank.bank_code,
          label: bank.name,
        })
      );

      const selected =
        options.find(
          (option) =>
            option.value === value
        ) || null;

      return (
        <div>
          <FieldLabel>
            {field.label}
          </FieldLabel>

          <Select
            options={options}
            styles={selectStyles}
            value={selected}
            onChange={(option) =>
              onChange(
                option?.value || ""
              )
            }
            isLoading={banksLoading}
            isSearchable
            placeholder="Select bank"
            noOptionsMessage={() =>
              banksLoading
                ? "Loading banks..."
                : "No banks found"
            }
          />

          {hasError && (
            <ErrorText>
              {errorMessage}
            </ErrorText>
          )}
        </div>
      );
    }

    return (
      <div>
        <FieldLabel>
          {field.label}
        </FieldLabel>

        <input
          type="text"
          value={value}
          onChange={(event) =>
            onChange(
              event.target.value
            )
          }
          placeholder={
            field.placeholder ||
            "Type bank name"
          }
          className={inputClass}
        />

        {hasError && (
          <ErrorText>
            {errorMessage}
          </ErrorText>
        )}
      </div>
    );
  }

  return (
    <div>
      <FieldLabel>
        {field.label}
        {field.optional
          ? " (optional)"
          : ""}
      </FieldLabel>

      <input
        type="text"
        value={value}
        onChange={(event) =>
          onChange(
            event.target.value
          )
        }
        placeholder={
          field.placeholder || ""
        }
        className={inputClass}
      />

      {hasError && (
        <ErrorText>
          {errorMessage}
        </ErrorText>
      )}
    </div>
  );
}

function SelectionCard({
  active,
  icon,
  label,
  onClick,
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`w-full flex items-center justify-between px-5 py-4 rounded-2xl border transition duration-200 ${
        active
          ? "bg-[#0A4730] border-[#0A4730] text-white shadow-sm"
          : "bg-[#C0F6B7]/40 border-[#0A4730]/10 text-[#0A4730]"
      }`}
    >
      <div className="flex items-center gap-3">
        <span
          className={
            active
              ? "text-white"
              : "text-[#0A4730]/70"
          }
        >
          {icon}
        </span>

        <span className="text-sm font-medium">
          {label}
        </span>
      </div>

      {active ? (
        <CheckCircle2
          size={18}
          className="text-white"
        />
      ) : (
        <Circle
          size={18}
          className="text-[#0A4730]/20"
        />
      )}
    </button>
  );
}